import React, { useEffect, useState } from "react";
import { observer, inject } from "mobx-react";
import { Loading } from "../UI";
import { useLocation, Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";

import AppBar from "./AppBar";
import AttachmentViewer from "../components/AttachmentViewer";
import styled from "styled-components";
import { useMobile } from "../utils/mobile";
import { useSessionSync } from "../utils/useSessionSync";

// App shell pinned to the visible screen. Sizing it with vh/dvh left it taller than the screen
// on iOS (Chrome especially, because of its toolbars), so the page scrolled and hid the header.
// A fixed box is exactly the visible area in every browser and leaves nothing behind it to scroll.
// Inner lists do their own scrolling (see MobileScrollList, which measures against this element).
const LayoutContainer = styled.div((props) => ({
  position: "fixed",
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  margin: 0,
  padding: 0,
  background: props.theme.color.white,
  color: props.theme.color.text,
  overflow: "hidden"
}));

// html/body don't scroll (see GlobalStyles), so the login screens scroll inside this box.
// Its top / height follow the visible area (see useVisibleArea): with the keyboard open it
// shrinks above it, so the form's button can still be scrolled into view
const LoginLayoutContainer = styled.div(({ theme }) => ({
  position: "fixed",
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  overflowY: "auto",
  overflowX: "hidden",
  WebkitOverflowScrolling: "touch",
  padding: 0,
  margin: 0,
  background: theme.color.background,
  color: "#FBFCFC",
}));

// The part of the screen not covered by the on-screen keyboard (null where unsupported).
// A fixed box stays the full screen height behind the keyboard, hiding what's under it
const useVisibleArea = (isEnabled) => {
  const [visibleArea, setVisibleArea] = useState(null);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!isEnabled || !viewport) return undefined;

    const update = () => setVisibleArea({ top: viewport.offsetTop, height: viewport.height });
    update();
    viewport.addEventListener("resize", update);
    viewport.addEventListener("scroll", update);
    return () => {
      viewport.removeEventListener("resize", update);
      viewport.removeEventListener("scroll", update);
    };
  }, [isEnabled]);

  return visibleArea;
};

const LoadingContainer = (props) => {
  const { isLoading, children } = props;
  if (isLoading) {
    return <Loading loading={isLoading} />;
  }
  return <>{children}</>;
};

const Layout = ({ userStore, children }) => {
  const { i18n } = useTranslation();
  let location = useLocation().pathname;
  const rtlOrLtr = i18n.dir();
  const isMobile = useMobile();
  // Log in / out / refresh the token together with the other open tabs
  useSessionSync();
  const isLoginScreen =
    location.includes("/login") ||
    location.includes("/forgot_password") ||
    location.includes("/new_password");
  const visibleArea = useVisibleArea(isLoginScreen);

  useEffect(() => {
    i18n.changeLanguage(userStore.language);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userStore.language]);

  useEffect(() => {
    i18n.changeLanguage(userStore.language);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userStore.language]);

  useEffect(() => {
    const body = document.body;
    body.dir = rtlOrLtr;
    document.documentElement.dir = rtlOrLtr;
    document.documentElement.lang = i18n.language;
  }, [rtlOrLtr, i18n.language]);

  return (
    <>
      {isLoginScreen ? (
        <LoginLayoutContainer
          style={
            visibleArea && { top: visibleArea.top, bottom: "auto", height: visibleArea.height }
          }
        >
          <LoadingContainer isLoading={false}>{children}</LoadingContainer>
          <Outlet />
        </LoginLayoutContainer>
      ) : (
        <LayoutContainer isMobile={isMobile} data-app-shell>
          <AppBar>
            {children}
            <Outlet />
          </AppBar>
          {/* Attachment viewer, opened from any page with openAttachment() */}
          <AttachmentViewer />
        </LayoutContainer>
      )}
    </>
  );
};

export default inject("userStore")(observer(Layout));
